import type { LucideIcon } from "lucide-react";
import { useId, type ReactNode } from "react";
import { cn } from "./cn";
import { Skeleton } from "./feedback";

export type SoftTone = "accent" | "info" | "success" | "warning" | "danger" | "neutral";

const ICON_TONES: Record<SoftTone, string> = {
  accent: "bg-accent-soft text-accent",
  info: "bg-info-soft text-info",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  neutral: "bg-surface-2 text-muted",
};

/** Icon inside a soft rounded-square background. The signature accent of the HostDock UI. */
export function IconBadge({ icon: Icon, tone = "accent", size = "md", className }: { icon: LucideIcon; tone?: SoftTone; size?: "sm" | "md" | "lg"; className?: string }) {
  const box = size === "lg" ? "h-11 w-11 rounded-xl" : size === "sm" ? "h-7 w-7 rounded-lg" : "h-10 w-10 rounded-xl";
  const px = size === "lg" ? 20 : size === "sm" ? 15 : 17;
  return (
    <span className={cn("flex shrink-0 items-center justify-center", box, ICON_TONES[tone], className)}>
      <Icon size={px} strokeWidth={1.9} />
    </span>
  );
}

/** A thin progress track. pct is clamped; tone defaults to severity (amber ≥75, red ≥90). */
export function ProgressBar({ pct, tone, label, className }: { pct: number; tone?: SoftTone; label?: string; className?: string }) {
  const p = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));
  const auto = p >= 90 ? "bg-danger" : p >= 75 ? "bg-warning" : "hd-gradient";
  const fill = tone ? { accent: "hd-gradient", info: "bg-info", success: "bg-success", warning: "bg-warning", danger: "bg-danger", neutral: "bg-surface-3" }[tone] : auto;
  return (
    <div className={cn("h-1.5 overflow-hidden rounded-full bg-surface-2", className)} role="meter" aria-label={label} aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-[width] duration-300", fill)} style={{ width: `${p}%` }} />
    </div>
  );
}

/**
 * Label + used/limit + progress bar. `used = null` renders "Unavailable" (never a fake 0%),
 * honouring the rule that unmeasured telemetry is shown as unavailable.
 */
export function UsageMeter({ label, used, limit, format = String, className }: { label: string; used: number | null; limit: number; format?: (n: number) => string; className?: string }) {
  const pct = used != null && limit > 0 ? (used / limit) * 100 : 0;
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between text-[13px]">
        <span className="text-muted">{label}</span>
        <span className="tabular-nums font-medium">{used == null ? <span className="text-subtle">Unavailable</span> : `${format(used)} / ${format(limit)}`}</span>
      </div>
      <ProgressBar className="mt-1.5" pct={used == null ? 0 : pct} label={label} tone={used == null ? "neutral" : undefined} />
    </div>
  );
}

/** Compact summary card: icon, label, big value, optional sub-line. */
export function MetricCard({ icon, tone = "accent", label, value, sub, loading }: { icon?: LucideIcon; tone?: SoftTone; label: string; value: ReactNode; sub?: ReactNode; loading?: boolean }) {
  return (
    <div className="hd-lift flex min-w-0 flex-col items-start gap-2.5 rounded-2xl border border-line bg-surface p-3.5 shadow-sm sm:flex-row sm:gap-3.5 sm:p-4">
      {icon && <IconBadge icon={icon} tone={tone} />}
      <div className="w-full min-w-0 flex-1">
        <p className="text-[12px] leading-snug font-medium text-muted sm:text-[12.5px]" title={label}>{label}</p>
        {loading ? <Skeleton className="mt-1.5 h-6 w-14" /> : <p className="mt-0.5 truncate text-[18px] leading-tight font-semibold tracking-tight tabular-nums sm:text-[22px]">{value}</p>}
        {sub && !loading && <div className="mt-0.5 truncate text-[11.5px] text-subtle sm:text-[12px]">{sub}</div>}
      </div>
    </div>
  );
}

/** Resource card with a usage meter (storage, memory, …). */
export function ResourceCard({ icon, tone = "accent", label, used, limit, format = String, foot }: { icon?: LucideIcon; tone?: SoftTone; label: string; used: number | null; limit: number; format?: (n: number) => string; foot?: ReactNode }) {
  return (
    <div className="hd-lift min-w-0 rounded-2xl border border-line bg-surface p-3.5 shadow-sm sm:p-4">
      <div className="flex items-center gap-2.5">
        {icon && <IconBadge icon={icon} tone={tone} size="sm" />}
        <p className="truncate text-[13px] font-medium">{label}</p>
      </div>
      <p className="mt-3 truncate text-[16px] font-semibold tracking-tight tabular-nums sm:text-[18px]">
        {used == null ? <span className="text-subtle">Unavailable</span> : <>{format(used)} <span className="text-[13px] font-normal text-subtle">/ {format(limit)}</span></>}
      </p>
      <ProgressBar className="mt-2" pct={used != null && limit > 0 ? (used / limit) * 100 : 0} label={label} tone={used == null ? "neutral" : undefined} />
      {foot && <p className="mt-2 text-[11.5px] text-subtle sm:text-[12px]">{foot}</p>}
    </div>
  );
}

/** Informational feature card: icon, title, one-line description. */
export function FeatureCard({ icon, tone = "accent", title, description }: { icon: LucideIcon; tone?: SoftTone; title: string; description: ReactNode }) {
  return (
    <div className="hd-lift rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <IconBadge icon={icon} tone={tone} />
      <p className="mt-3 text-[13.5px] font-semibold">{title}</p>
      <p className="mt-1 text-[12.5px] leading-snug text-muted">{description}</p>
    </div>
  );
}

/** A settings row: leading icon, title + description, trailing control. */
export function SettingRow({ icon, title, description, control, className }: { icon?: LucideIcon; title: ReactNode; description?: ReactNode; control?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 py-3.5", className)}>
      <div className="flex min-w-0 items-start gap-3">
        {icon && <IconBadge icon={icon} tone="neutral" size="sm" />}
        <div className="min-w-0">
          <p className="text-[13.5px] font-medium">{title}</p>
          {description && <p className="mt-0.5 text-[12.5px] text-muted">{description}</p>}
        </div>
      </div>
      {control && <div className="shrink-0">{control}</div>}
    </div>
  );
}

/**
 * Circular usage ring. `used = null` renders "Unavailable" with an empty track,
 * never a fabricated percentage. Exposes role="meter" like ProgressBar.
 */
export function RingMeter({ label, used, limit, format = String, size = 88, className }: { label: string; used: number | null; limit: number; format?: (n: number) => string; size?: number; className?: string }) {
  const pct = used != null && limit > 0 ? Math.max(0, Math.min(100, (used / limit) * 100)) : 0;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const gid = useId();
  const color = pct >= 90 ? "var(--hd-danger)" : pct >= 75 ? "var(--hd-warning)" : `url(#${gid})`;
  return (
    <div className={cn("flex items-center gap-3.5", className)}>
      <div className="relative shrink-0" style={{ width: size, height: size }} role="meter" aria-label={label} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" style={{ stopColor: "var(--hd-logo-1, #8b5cf6)" }} />
              <stop offset="100%" style={{ stopColor: "var(--hd-logo-3, #4f46e5)" }} />
            </linearGradient>
          </defs>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--hd-surface-3)" strokeWidth={stroke} />
          {used != null && pct > 0 && (
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} className="transition-[stroke-dashoffset] duration-500" />
          )}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[14px] font-semibold tabular-nums">{used == null ? "—" : `${Math.round(pct)}%`}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-medium">{label}</p>
        <p className="mt-0.5 text-[12.5px] tabular-nums text-muted">{used == null ? "Unavailable" : `${format(used)} of ${format(limit)}`}</p>
      </div>
    </div>
  );
}

/** Large tappable shortcut tile used for quick actions. Rendered by the caller's link component. */
export function ActionTileBody({ icon, tone = "accent", title, description }: { icon: LucideIcon; tone?: SoftTone; title: string; description?: ReactNode }) {
  return (
    <span className="flex h-full items-start gap-3">
      <IconBadge icon={icon} tone={tone} />
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold text-fg">{title}</span>
        {description && <span className="mt-0.5 block text-[12.5px] leading-snug text-muted">{description}</span>}
      </span>
    </span>
  );
}
export const actionTileClass = "hd-lift group block rounded-2xl border border-line bg-surface p-4 shadow-sm focus-visible:outline-2";
